import { useCallback, useEffect, useRef, useState } from 'react';
import { messagingAPI } from '@/services/api';
import NativeWebRTCTransport from './NativeWebRTCTransport';

const terminal = new Set(['declined', 'cancelled', 'missed', 'failed', 'ended']);
const RECOVERY_GRACE_MS = 2_000;
const RECOVERY_TIMEOUT_MS = 12_000;
const MAX_RECOVERY_ATTEMPTS = 3;

export const useConversationCall = ({ socket, connected, conversation, sendCallEvent, onIncomingCall }) => {
  const transportRef = useRef(null);
  const acceptingRef = useRef(false);
  const [accepting, setAccepting] = useState(false);
  const remoteStreamRef = useRef(null);
  const terminalTimerRef = useRef(null);
  const recoveryGraceRef = useRef(null);
  const recoveryTimeoutRef = useRef(null);
  const recoveryAttemptsRef = useRef(0);
  const recoveryIdRef = useRef(0);
  const callRef = useRef(null);
  const sendCallEventRef = useRef(sendCallEvent);
  const [call, setCall] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [connectionState, setConnectionState] = useState('idle');
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { callRef.current = call; }, [call]);
  useEffect(() => { sendCallEventRef.current = sendCallEvent; }, [sendCallEvent]);

  const clearRecoveryTimers = useCallback(() => {
    if (recoveryGraceRef.current) clearTimeout(recoveryGraceRef.current);
    if (recoveryTimeoutRef.current) clearTimeout(recoveryTimeoutRef.current);
    recoveryGraceRef.current = null;
    recoveryTimeoutRef.current = null;
  }, []);

  const cleanup = useCallback(() => {
    clearRecoveryTimers();
    recoveryAttemptsRef.current = 0;
    recoveryIdRef.current = 0;
    transportRef.current?.stop();
    transportRef.current = null;
    remoteStreamRef.current = null;
    setLocalStream(null);
    setRemoteStream(null);
    setConnectionState('idle');
    setMuted(false);
    setCameraOff(false);
  }, [clearRecoveryTimers]);

  const showTerminalThenClear = useCallback((payload) => {
    if (terminalTimerRef.current) clearTimeout(terminalTimerRef.current);
    callRef.current = callRef.current ? { ...callRef.current, ...payload } : null;
    setCall((current) => current ? { ...current, ...payload } : null);
    cleanup();
    terminalTimerRef.current = setTimeout(() => setCall(null), 2500);
  }, [cleanup]);

  const failRecovery = useCallback((callId) => {
    if (callRef.current?.call_id !== callId || terminal.has(callRef.current?.status)) return;
    setError('The media connection could not be restored.');
    showTerminalThenClear({ status: 'failed', terminal_reason: 'media_connection_lost' });
    sendCallEventRef.current('call_end', { call_id: callId }).catch(() => {});
  }, [showTerminalThenClear]);

  const sendRecoveryOffer = useCallback(async (callId, recoveryId) => {
    if (callRef.current?.call_id !== callId || terminal.has(callRef.current?.status)) return;
    const offer = await transportRef.current?.createRecoveryOffer();
    if (offer && callRef.current?.call_id === callId && !terminal.has(callRef.current?.status) && recoveryIdRef.current === recoveryId) {
      await sendCallEventRef.current('call_offer', { call_id: callId, recovery_id: recoveryId, description: offer });
    }
  }, []);

  const beginRecovery = useCallback(async (callId, requestedRecoveryId = null) => {
    const activeCall = callRef.current;
    if (activeCall?.call_id !== callId || activeCall.status !== 'accepted') return;
    if (recoveryTimeoutRef.current) return;
    const recoveryId = requestedRecoveryId ?? recoveryIdRef.current + 1;
    if (recoveryId <= recoveryIdRef.current) return;
    if (recoveryId > MAX_RECOVERY_ATTEMPTS) {
      failRecovery(callId);
      return;
    }
    recoveryIdRef.current = recoveryId;
    recoveryAttemptsRef.current += 1;
    if (recoveryAttemptsRef.current > MAX_RECOVERY_ATTEMPTS) {
      await failRecovery(callId);
      return;
    }
    setConnectionState('reconnecting');
    recoveryTimeoutRef.current = setTimeout(() => failRecovery(callId), RECOVERY_TIMEOUT_MS);
    try {
      if (activeCall.direction === 'outgoing') await sendRecoveryOffer(callId, recoveryId);
      else await sendCallEventRef.current('call_restart', { call_id: callId, recovery_id: recoveryId });
    } catch {
      clearRecoveryTimers();
      await failRecovery(callId);
    }
  }, [clearRecoveryTimers, failRecovery, sendRecoveryOffer]);

  const onTransportStateChange = useCallback((state) => {
    const activeCall = callRef.current;
    if (!activeCall || terminal.has(activeCall.status)) return;
    if (state === 'connected') {
      clearRecoveryTimers();
      setConnectionState('connected');
      return;
    }
    if (state === 'failed') {
      beginRecovery(activeCall.call_id);
      return;
    }
    if (state === 'disconnected') {
      setConnectionState('reconnecting');
      if (!recoveryGraceRef.current && !recoveryTimeoutRef.current) {
        recoveryGraceRef.current = setTimeout(() => {
          recoveryGraceRef.current = null;
          beginRecovery(activeCall.call_id);
        }, RECOVERY_GRACE_MS);
      }
      return;
    }
    setConnectionState(state);
  }, [beginRecovery, clearRecoveryTimers]);

  const makeTransport = useCallback(async (mediaType, callId) => {
    const config = await messagingAPI.getIceServers(callId);
    if (config.degraded) setError(config.warning);
    const transport = new NativeWebRTCTransport({
      iceServers: config.ice_servers || [],
      onIceCandidate: (candidate) => sendCallEvent('call_ice', { call_id: callId, recovery_id: recoveryIdRef.current, candidate }).catch(() => {}),
      onTrack: (stream) => {
        remoteStreamRef.current = stream;
        setRemoteStream(stream);
      },
      onConnectionStateChange: onTransportStateChange,
    });
    transportRef.current = transport;
    const stream = await transport.initialize(mediaType);
    setLocalStream(stream);
    return transport;
  }, [onTransportStateChange, sendCallEvent]);

  const startCall = useCallback(async (mediaType) => {
    if (!conversation || !connected) throw new Error('Reconnect before starting a call.');
    if (callRef.current && !terminal.has(callRef.current.status)) {
      throw new Error('Finish the current call before starting another.');
    }
    setError(null);
    const result = await sendCallEvent('call_start', {
      conversation_id: conversation.conversation_id,
      media_type: mediaType,
    });
    const nextCall = { ...result, direction: 'outgoing' };
    callRef.current = nextCall;
    setCall(nextCall);
    try {
      await makeTransport(mediaType, result.call_id);
      setConnectionState('ringing');
    } catch (cause) {
      await sendCallEvent('call_cancel', { call_id: result.call_id }).catch(() => {});
      setCall(null);
      cleanup();
      throw cause;
    }
  }, [cleanup, connected, conversation, makeTransport, sendCallEvent]);

  const acceptCall = useCallback(async () => {
    if (!call || acceptingRef.current) return;
    acceptingRef.current = true;
    setAccepting(true);
    try {
      setError(null);
      // Media must be ready before the caller is told to send its offer;
      // otherwise a fast offer can arrive before RTCPeerConnection exists.
      await makeTransport(call.media_type, call.call_id);
      if (callRef.current?.call_id !== call.call_id || callRef.current.status !== 'ringing') {
        cleanup();
        return;
      }
      const accepted = await sendCallEvent('call_accept', { call_id: call.call_id });
      setCall((current) => ({ ...current, ...accepted, status: 'accepted' }));
      setConnectionState('connecting');
    } catch (cause) {
      setError(cause.message || 'Unable to accept this call.');
      cleanup();
    } finally {
      acceptingRef.current = false;
      setAccepting(false);
    }
  }, [call, cleanup, makeTransport, sendCallEvent]);

  const declineCall = useCallback(async () => {
    if (!call) return;
    await sendCallEvent('call_decline', { call_id: call.call_id }).catch(() => {});
    setCall(null);
    cleanup();
  }, [call, cleanup, sendCallEvent]);

  const endCall = useCallback(async () => {
    if (!call) return;
    const event = call.direction === 'outgoing' && call.status === 'ringing' ? 'call_cancel' : 'call_end';
    callRef.current = null;
    setCall(null);
    cleanup();
    await sendCallEvent(event, { call_id: call.call_id }).catch(() => {});
  }, [call, cleanup, sendCallEvent]);

  const toggleMute = useCallback(() => {
    setMuted(transportRef.current?.toggleMute() ?? false);
  }, []);
  const toggleCamera = useCallback(() => {
    setCameraOff(transportRef.current?.toggleCamera() ?? false);
  }, []);
  const switchCamera = useCallback(async () => transportRef.current?.switchCamera(), []);

  useEffect(() => {
    if (!socket) return undefined;
    const incoming = (payload) => {
      const activeCall = callRef.current;
      if (activeCall && !terminal.has(activeCall.status) && activeCall.call_id !== payload.call_id) {
        sendCallEventRef.current('call_decline', { call_id: payload.call_id }).catch(() => {});
        setError('Another call was declined while your current call is active.');
        return;
      }
      setError(null);
      onIncomingCall?.(payload);
      const nextCall = { ...payload, direction: 'incoming' };
      callRef.current = nextCall;
      setCall(nextCall);
      setConnectionState('ringing');
    };
    const accepted = async (payload) => {
      if (payload.call_id !== call?.call_id || call?.direction !== 'outgoing') return;
      setCall((current) => ({ ...current, ...payload, status: 'accepted' }));
      try {
        const offer = await transportRef.current?.createOffer();
        if (offer) await sendCallEvent('call_offer', { call_id: payload.call_id, description: offer });
      } catch (cause) {
        setError('Connection failed. Please try again.');
        endCall();
      }
    };
    const offer = async (payload) => {
      if (payload.call_id !== call?.call_id || call?.direction !== 'incoming') return;
      const recoveryId = payload.recovery_id || 0;
      if (recoveryId < recoveryIdRef.current) return;
      if (recoveryId > recoveryIdRef.current) {
        recoveryIdRef.current = recoveryId;
        setConnectionState('reconnecting');
      }
      try {
        const answer = await transportRef.current?.applyOfferAndCreateAnswer(payload.description);
        if (answer && recoveryIdRef.current === recoveryId) await sendCallEvent('call_answer', { call_id: payload.call_id, recovery_id: recoveryId, description: answer });
      } catch {
        setError('Connection failed. Please try again.');
      }
    };
    const answer = async (payload) => {
      if (payload.call_id === call?.call_id && (payload.recovery_id || 0) === recoveryIdRef.current) await transportRef.current?.applyAnswer(payload.description);
    };
    const restart = async (payload) => {
      if (payload.call_id === call?.call_id && call?.direction === 'outgoing') {
        await beginRecovery(payload.call_id, payload.recovery_id);
      }
    };
    const ice = async (payload) => {
      if (payload.call_id === call?.call_id && (payload.recovery_id || 0) === recoveryIdRef.current) await transportRef.current?.addIceCandidate(payload.candidate);
    };
    const terminalEvent = (payload) => {
      if (payload.call_id === call?.call_id) {
        showTerminalThenClear(payload);
      }
    };
    socket.on('incoming_call', incoming);
    socket.on('call_accept', accepted);
    socket.on('call_offer', offer);
    socket.on('call_answer', answer);
    socket.on('call_ice', ice);
    socket.on('call_restart', restart);
    ['call_decline', 'call_cancel', 'call_end'].forEach((event) => socket.on(event, terminalEvent));
    return () => {
      socket.off('incoming_call', incoming);
      socket.off('call_accept', accepted);
      socket.off('call_offer', offer);
      socket.off('call_answer', answer);
      socket.off('call_ice', ice);
      socket.off('call_restart', restart);
      ['call_decline', 'call_cancel', 'call_end'].forEach((event) => socket.off(event, terminalEvent));
    };
  }, [beginRecovery, call?.call_id, call?.direction, endCall, onIncomingCall, sendCallEvent, showTerminalThenClear, socket]);

  useEffect(() => {
    if (!call || call.status !== 'ringing') return undefined;
    const timer = setTimeout(() => {
      sendCallEvent('call_end', { call_id: call.call_id })
        .then((payload) => showTerminalThenClear(payload))
        .catch(() => showTerminalThenClear({ status: 'failed', terminal_reason: 'timeout' }));
    }, 45_000);
    return () => clearTimeout(timer);
  }, [call, sendCallEvent, showTerminalThenClear]);

  useEffect(() => () => {
    if (terminalTimerRef.current) clearTimeout(terminalTimerRef.current);
    const activeCall = callRef.current;
    if (activeCall && !terminal.has(activeCall.status)) {
      const event = activeCall.direction === 'outgoing' && activeCall.status === 'ringing'
        ? 'call_cancel'
        : 'call_end';
      sendCallEventRef.current(event, { call_id: activeCall.call_id }).catch(() => {});
    }
    cleanup();
  }, [cleanup]);

  return {
    call,
    localStream,
    remoteStream,
    connectionState,
    muted,
    cameraOff,
    error,
    startCall,
    acceptCall,
    accepting,
    declineCall,
    endCall,
    toggleMute,
    toggleCamera,
    switchCamera,
    enabled: Boolean(conversation && connected && (!call || terminal.has(call.status))),
    isTerminal: terminal.has(call?.status),
  };
};

export default useConversationCall;
